import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AgentListItem } from '~pages/SystemManagement/domain';

import { batchAsyncSettingsUpdate, batchGroupChangeUpdate, getAgents, getAgentsByNextUrl } from './api';
import { AgentsResponse, BatchAsyncSettingsUpdate, BatchGroupChangeUpdate } from './domain';

type Options = {
  accessFilterId?: number;
  diallerGroupId?: number;
  allocated?: boolean;
  disabled?: boolean;
  shouldFetch?: boolean;
};

const useAgentList = (
  search: string,
  options: Options = {
    diallerGroupId: undefined,
    allocated: undefined,
    disabled: undefined,
    shouldFetch: true,
  },
) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const accessFilterId = options.accessFilterId;
  const diallerGroupId = options.diallerGroupId;
  const allocated = options.allocated;
  const disabled = options.disabled;
  const shouldFetch = options.shouldFetch ?? true;

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: AgentsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getAgentsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setAgents((prev) => [...prev, ...resp!.agents]);
      setTotal(resp.totalAgents);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: AgentsResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getAgents(search, diallerGroupId, allocated, disabled, accessFilterId, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setAgents(resp.agents);
    setTotal(resp.totalAgents);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, diallerGroupId, allocated, disabled, accessFilterId]);

  const bulkAsyncSettingsUpdate = useCallback(
    async (data: BatchAsyncSettingsUpdate) => {
      try {
        await batchAsyncSettingsUpdate(accessFilterId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [accessFilterId, reload],
  );

  const bulkGroupChangeUpdate = useCallback(
    async (data: BatchGroupChangeUpdate) => {
      try {
        await batchGroupChangeUpdate(accessFilterId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [accessFilterId, reload],
  );

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setAgents([]);
  }, [search, diallerGroupId, allocated, disabled, accessFilterId, shouldFetch]);

  useEffect(() => {
    const load = async (
      search: string,
      diallerGroupId: number | undefined,
      allocated: boolean | undefined,
      disabled: boolean | undefined,
      accessFilterId: number | undefined,
    ) => {
      setLoading(true);
      setError(false);

      let resp: AgentsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getAgents(search, diallerGroupId, allocated, disabled, accessFilterId, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setAgents((prev) => [...prev, ...resp!.agents]);
      setTotal(resp.totalAgents);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, diallerGroupId, allocated, disabled, accessFilterId);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, diallerGroupId, allocated, disabled, accessFilterId, shouldFetch]);

  return {
    loading,
    error,
    agents,
    total,
    hasMore,
    reload,
    intersectionObserverRef,
    bulkAsyncSettingsUpdate,
    bulkGroupChangeUpdate,
  };
};

export default useAgentList;
